import axios from "axios";
import { useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";

import BASE_URL from "../constants/baseURL";
import { alert } from "../redux/slices/alertSlice";
import { apiError } from "../redux/slices/loaderSlice";
import { deleteAccessToken } from "../redux/slices/memberSlice";
import { useNavigate } from "react-router-dom";
import {
  isSessionExpired,
  markSessionExpired,
  setSessionExpiry,
} from "../utils/session";

export default function useAxios({
  baseURL = "mox_api",
  customBaseURL = "",
  customAuthToken = "",
  disableAuthToken = false,
} = {}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { authToken } = useSelector((state) => state.memberDetails);

  // Only requests riding on the login session are subject to the expiry
  // pre-check; custom-token / token-less calls are exempt.
  const usesSessionToken = !disableAuthToken && !customAuthToken;

  const axiosInstance = useRef(axios.create());

  useEffect(() => {
    axiosInstance.current = axios.create({
      // withCredentials: true,

      headers: {
        ...(!disableAuthToken && {
          Authorization: "Bearer " + (customAuthToken || authToken),
        }),
        "x-api-key": "ziYSCXNw9J50ayP3HdV9N2hC3FL90SIL4OUOmB4T",
      },

      ...(Boolean(customBaseURL)
        ? { baseURL: customBaseURL }
        : {
            baseURL: BASE_URL[baseURL].url + BASE_URL[baseURL].version,
          }),

      validateStatus: (status) => status < 500,
    });
  }, [authToken, baseURL, customAuthToken, customBaseURL, disableAuthToken]);

  return useCallback(
    ({
      url,
      method = "GET",
      data,
      params,
      disableRedirect = false,
      disableError = false,
      customAuthToken,
    }) => {
      // The session lapsed while the page was idle (e.g. a suspended tab
      // whose expiry timer never fired): sign out instead of firing a call
      // that is guaranteed to 401.
      if (usesSessionToken && !customAuthToken && isSessionExpired()) {
        markSessionExpired();
        dispatch(deleteAccessToken());
        // deleteAccessToken hard-redirects to the login page; stay pending
        // so callers don't run their error paths mid-navigation.
        return new Promise(() => {});
      }

      return axiosInstance
        .current({
          url: url,
          method: method,
          data: data,
          params: params,
          // withCredentials: true,
          headers: {
            ...(!disableAuthToken && {
              Authorization: "Bearer " + (customAuthToken || authToken),
            }),
          },
        })
        .then((response) => {
          console.log(response.status, "response.data.detail?.code");

          if (
            (response.status >= 500 || response.status == 404) &&
            !disableRedirect
          ) {
            navigate("/error-500"); // 👈 redirect to custom error page
            return;
          }

          if (
            response.data.detail?.code === "#401" ||
            response.data.detail?.code === "1401" ||
            response.data.detail?.code === "401"
          ) {
            let autoRefresh = response.data.detail.auto_refresh;
            let refreshToken = response.data.detail.refresh_token;
            if (!autoRefresh) {
              // Shown after the redirect via the sessionExpired flag (an
              // in-memory alert wouldn't survive the full page load).
              markSessionExpired();
              return dispatch(deleteAccessToken());
            } else {
              const isGuardToken =
                sessionStorage.getItem("auth_token_with_guard") ||
                localStorage.getItem("auth_token_with_guard");

              sessionStorage.removeItem("authToken");
              localStorage.removeItem("authToken");
              sessionStorage.removeItem("auth_token_with_guard");
              localStorage.removeItem("auth_token_with_guard");

              sessionStorage.setItem("authToken", refreshToken);
              localStorage.setItem("authToken", refreshToken);
              if (isGuardToken) {
                sessionStorage.setItem("auth_token_with_guard", refreshToken);
                localStorage.setItem("auth_token_with_guard", refreshToken);
              }
              setSessionExpiry(null, refreshToken);
              window.location.reload();
            }
          }

          if (response.status > 201 || !response.data.status) {
            if (!disableError) {
              dispatch(
                alert({
                  message:
                    response.data.message?.displayMessage ||
                    "Something went wrong!",
                  type: "error",
                })
              );
            }
            // console.log(response, "!response!");

            if (!disableRedirect) {
              dispatch(apiError(response.data?.message?.traceID));

              throw new Error();
            } else {
              return {
                status: false,
                code: response.data?.code,
                message: response.data.error,
                data: response.data.data,
                displayMessage:
                  response.data.message?.displayMessage ||
                  "Something went wrong!",
                traceID: response.data?.message?.traceID,
              };
            }
          }

          return response.data;
        })
        .catch((error) => {
          // ✅ Network / CORS / 500 error handling
          // if (
          //   !error.response || // CORS or network
          //   error.code === "ERR_NETWORK" ||
          //   (error.response && error.response.status >= 500)
          // ) {
          //   navigate("/error-500"); // 👈 redirect to custom error page
          //   return;
          // }

          if (!disableError) {
            dispatch(
              alert({
                message: "Something went wrong!",
                type: "error",
              })
            );
          }
        });
    },
    [dispatch, usesSessionToken]
  );
}
